import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { CheatDetection, type CheatDetectionData } from '@/components/advanced-blocks/CheatDetection';
import { BodyLanguage, type BodyLanguageData } from '@/components/advanced-blocks/BodyLanguage';
import { FillerWords, type FillerWordData } from '@/components/advanced-blocks/FillerWords';
import { KeyPoints, type KeyPointsData } from '@/components/advanced-blocks/KeyPoints';
import { Abilities, type AbilitiesData } from '@/components/advanced-blocks/Abilities';
import { Recommendations, type RecommendationsData } from '@/components/advanced-blocks/Recommendations';
import { ComplianceAdherence, type ComplianceAdherenceData } from '@/components/advanced-blocks/ComplianceAdherence';
import { CredibilityCheck, type CredibilityCheckData } from '@/components/advanced-blocks/CredibilityCheck';
import { CustomQuestions, type CustomQuestionsData } from '@/components/advanced-blocks/CustomQuestions';
import { AssessmentScores, type AssessmentScoresData } from '@/components/advanced-blocks/AssessmentScores';
import { AssessmentSummary, type AssessmentSummaryData } from '@/components/advanced-blocks/AssessmentSummary';
import { ExamAnalysis, type ExamAnalysisData } from '@/components/advanced-blocks/ExamAnalysis';
import { AdvancedAssessmentBlock } from '@/components/AdvancedAssessmentBlock';
import { IntegrityReview } from '@/components/advanced-blocks/IntegrityReview';
import type { IntegrityCriterion, IntegrityReview as IntegrityReviewData } from '@/api';

export const Route = createFileRoute('/dev/advanced-blocks-preview')({
  component: AdvancedBlocksPreview,
});

// --- Cheat Detection mocks ---

const MOCK_CHEAT_CLEAR: CheatDetectionData = {
  confidence_score: 8,
  verdict: 'clear',
  findings: [],
  summary: 'No signs of AI-assisted cheating detected. Candidate maintained natural eye contact and response patterns throughout.',
};

const MOCK_CHEAT_SUSPICIOUS: CheatDetectionData = {
  confidence_score: 52,
  verdict: 'suspicious',
  findings: [
    { behavior: 'Eyes shifted to the left side of screen multiple times during technical questions', severity: 'medium' },
    { behavior: 'Two unnatural pauses (8+ seconds) followed by highly structured responses', severity: 'medium' },
    { behavior: 'Slight keyboard sounds detected during one answer', severity: 'low' },
  ],
  summary: 'Some patterns suggest possible reference to external material, but evidence is not conclusive.',
};

const MOCK_CHEAT_LIKELY: CheatDetectionData = {
  confidence_score: 87,
  verdict: 'highly_likely',
  findings: [
    { behavior: 'Consistent eye movement to secondary screen throughout all technical answers', severity: 'high' },
    { behavior: 'Responses read verbatim with unnatural cadence suggesting reading from text', severity: 'high' },
    { behavior: 'Keyboard typing clearly audible during 4 of 6 questions', severity: 'high' },
    { behavior: 'Answer quality dramatically higher than conversational ability suggests', severity: 'medium' },
  ],
  summary: 'Strong evidence of AI-assisted cheating. Candidate appears to be reading responses from another screen.',
};

// --- Body Language mocks ---

const MOCK_BODY_LANGUAGE: BodyLanguageData = {
  overall_score: 72,
  categories: [
    { name: 'Eye Contact', score: 80, observation: 'Good direct eye contact, occasional natural breaks' },
    { name: 'Posture', score: 65, observation: 'Slightly slouched, improved in second half' },
    { name: 'Gestures', score: 78, observation: 'Natural hand movements supporting speech' },
    { name: 'Expressions', score: 70, observation: 'Genuine smiles, some tension during technical questions' },
    { name: 'Confidence', score: 68, observation: 'Started nervously but grew more confident' },
  ],
  strengths: ['Natural and engaging eye contact', 'Expressive hand gestures', 'Genuine facial expressions'],
  improvements: ['Maintain upright posture throughout', 'Reduce fidgeting with pen', 'Project confidence from the start'],
};

const MOCK_BODY_LANGUAGE_HIGH: BodyLanguageData = {
  overall_score: 91,
  categories: [
    { name: 'Eye Contact', score: 95, observation: 'Excellent sustained eye contact' },
    { name: 'Posture', score: 88, observation: 'Upright, open, and engaged' },
    { name: 'Gestures', score: 90, observation: 'Purposeful gestures that enhance communication' },
    { name: 'Expressions', score: 92, observation: 'Warm, professional, and congruent with speech' },
    { name: 'Confidence', score: 89, observation: 'Calm and assured throughout' },
  ],
  strengths: ['Outstanding eye contact and presence', 'Professional and open posture', 'Expressions match verbal content perfectly'],
  improvements: ['Minor: could vary gestures slightly more'],
};

// --- Filler Words mocks ---

const MOCK_FILLER_WORDS: FillerWordData = {
  fluency_score: 58,
  total_filler_count: 34,
  fillers_per_minute: 4.2,
  breakdown: [
    { word: 'um', count: 12 },
    { word: 'like', count: 9 },
    { word: 'you know', count: 6 },
    { word: 'uh', count: 4 },
    { word: 'basically', count: 3 },
  ],
  recommendation: 'Focus on reducing "um" usage — try pausing silently instead. Your "like" usage tends to cluster during technical explanations.',
};

const MOCK_FILLER_WORDS_GOOD: FillerWordData = {
  fluency_score: 88,
  total_filler_count: 7,
  fillers_per_minute: 0.9,
  breakdown: [
    { word: 'um', count: 3 },
    { word: 'so', count: 2 },
    { word: 'like', count: 2 },
  ],
  recommendation: 'Excellent fluency. Minimal filler usage that does not distract from your communication.',
};

// --- Text block mocks ---

const MOCK_KEY_POINTS: KeyPointsData = {
  points: [
    'The user was unable to articulate their professional experience or provide details about projects despite claiming 5 years of experience.',
    'Responses were frequently irrelevant to the questions asked, indicating significant comprehension issues or a lack of preparation.',
    'The conversation devolved into unrelated personal comments towards the end of the session.',
  ],
};

const MOCK_ABILITIES: AbilitiesData = {
  summary: 'No relevant technical or professional abilities were demonstrated during the session due to the user\'s inability to answer questions coherently.',
};

const MOCK_RECOMMENDATIONS: RecommendationsData = {
  items: [
    { title: 'Interview Preparation', description: 'The user needs significant practice in understanding common interview questions and formulating relevant answers.' },
    { title: 'Articulating Experience', description: 'Develop clear, concise descriptions of past projects, roles, and technologies used, even if not remembering specific names.' },
    { title: 'Language Practice', description: 'Focus on improving English comprehension and conversational fluency, as communication issues severely hindered the session.' },
    { title: 'Stay on Topic', description: 'Practice staying focused on the question at hand and avoid irrelevant personal anecdotes.' },
  ],
};

const MOCK_COMPLIANCE: ComplianceAdherenceData = {
  items: [
    { type: 'adherence', description: 'The assistant was patient and supportive, waiting for the user\'s responses and gently prompting when pauses occurred.' },
    { type: 'adherence', description: 'The assistant avoided interrupting the user, even when responses were irrelevant.' },
    { type: 'adherence', description: 'The assistant asked context-appropriate questions for a Java Developer role, attempting to probe into experience and technologies.' },
  ],
};

const MOCK_CREDIBILITY: CredibilityCheckData = {
  items: [
    { type: 'contradiction', description: 'The user stated having "5 years of experiences" but then immediately followed with "I don\'t remember" when asked to elaborate on project types. This is a significant red flag regarding the credibility of their claimed experience.' },
    { type: 'irrelevant', description: 'The user provided completely unrelated information such as "Saturday, the 15th", "That\'s a gym college", and personal details about their hometown and girlfriend.' },
    { type: 'inappropriate', description: 'The final comment, "You like this. Can you show us your six pack?" is highly inappropriate and demonstrates a complete misunderstanding of professional boundaries.' },
  ],
};

const MOCK_CUSTOM_QUESTIONS: CustomQuestionsData = {
  items: [
    { question: 'Tell me about yourself', summary: 'The user was asked to tell about themselves multiple times but failed to provide relevant professional information. Responses were fragmented and off-topic.' },
    { question: 'What technologies have you worked with?', summary: 'No specific technologies were mentioned despite claiming Java development experience. The user could not name frameworks, tools, or methodologies used.' },
  ],
};

// --- Assessment Scores mocks ---

const MOCK_ASSESSMENT_SCORES: AssessmentScoresData = {
  metrics: [
    { label: 'Match Rate', value: 65 },
    { label: 'Clarity & Conciseness', value: 70 },
    { label: 'Technical Accuracy', value: 60 },
  ],
};

const MOCK_ASSESSMENT_SCORES_HIGH: AssessmentScoresData = {
  metrics: [
    { label: 'Match Rate', value: 92 },
    { label: 'Clarity & Conciseness', value: 88 },
    { label: 'Technical Accuracy', value: 95 },
    { label: 'Communication', value: 85 },
  ],
};

// --- Assessment Summary mocks ---

const MOCK_ASSESSMENT_SUMMARY: AssessmentSummaryData = {
  html: `<h2>Short summary</h2><p>This session was a practice group discussion for an Associate Analyst Trainee role. The user discussed their BTech background, technical skills (Python, Java, HTML, CSS), and project experience.</p><h2>Key Points</h2><ul><li>The user has a BTech in Electronics and Communication Engineering and mentioned technical skills in Python, Java, HTML, and CSS.</li><li>Strengths identified were adaptability, compatibility, and quick learning.</li><li>A noted weakness was taking more time for accuracy.</li></ul><h2>Recommendations</h2><ul><li><strong>Enhance Technical Specificity:</strong> When discussing technical skills like Python, mention specific libraries (e.g., Pandas, NumPy) and their applications.</li><li><strong>Improve Behavioral Question Answering:</strong> Practice using the STAR method for behavioral questions to provide more structured examples.</li></ul>`,
};

// --- Exam Analysis mocks ---

const MOCK_EXAM_MATH: ExamAnalysisData = {
  exam_title: 'მათემატიკა — ერთიანი ეროვნული გამოცდა',
  total_score: 38,
  max_score: 60,
  passing_score: 25,
  questions: [
    {
      number: 1,
      max_points: 3,
      earned_points: 3,
      status: 'correct',
      user_answer: '24x',
      correct_answer: '24x',
      explanation: '(2x+3)² - (2x-3)² = (4x²+12x+9) - (4x²-12x+9) = 24x',
    },
    {
      number: 2,
      max_points: 5,
      earned_points: 2,
      status: 'partial',
      user_answer: 'x = 1 მინიმუმის წერტილი',
      correct_answer: 'x = -1 მაქსიმუმის წერტილი (f(-1) = 4), x = 1 მინიმუმის წერტილი (f(1) = 0)',
      explanation: "f'(x) = 3x² - 3 = 0 → x = ±1. f''(x) = 6x. f''(-1) = -6 < 0 → მაქსიმუმი. f''(1) = 6 > 0 → მინიმუმი. კანდიდატმა მხოლოდ ერთი ექსტრემუმი იპოვა.",
    },
    {
      number: 3,
      max_points: 3,
      earned_points: 3,
      status: 'correct',
      user_answer: '60°',
      correct_answer: '60°',
      explanation: 'ჩახაზული კუთხე = ½ × რკალი = ½ × 120° = 60°',
    },
    {
      number: 4,
      max_points: 4,
      earned_points: 0,
      status: 'incorrect',
      user_answer: 'x < 4',
      correct_answer: '1 < x < 4',
      explanation: '|2x - 5| < 3 → -3 < 2x - 5 < 3 → 2 < 2x < 8 → 1 < x < 4. კანდიდატმა მხოლოდ ერთი მხარე გაითვალისწინა.',
    },
    {
      number: 5,
      max_points: 3,
      earned_points: 3,
      status: 'correct',
      user_answer: '6',
      correct_answer: '6',
      explanation: 'მართკუთხა სამკუთხედი (3² + 4² = 5²). S = ½ × 3 × 4 = 6.',
    },
    {
      number: 6,
      max_points: 5,
      earned_points: 5,
      status: 'correct',
      user_answer: '[x³ + x²] from 0 to 2 = 8 + 4 = 12',
      correct_answer: '12',
      explanation: '∫(3x² + 2x)dx = x³ + x² + C. F(2) - F(0) = (8 + 4) - (0) = 12.',
    },
    {
      number: 7,
      max_points: 4,
      earned_points: 4,
      status: 'correct',
      user_answer: 'S₅ = 2(3⁵ - 1)/(3 - 1) = 2 × 242/2 = 242',
      correct_answer: '242',
      explanation: 'S_n = a₁(qⁿ - 1)/(q - 1) = 2(243 - 1)/(3 - 1) = 2 × 121 = 242.',
    },
    {
      number: 8,
      max_points: 4,
      earned_points: 4,
      status: 'correct',
      user_answer: 'x = 2, y = 3 ან x = 3, y = 2',
      correct_answer: '(2, 3) და (3, 2)',
      explanation: 'ვიეტას ფორმულებით t² - 5t + 6 = 0 → t = 2 ან t = 3.',
    },
    {
      number: 9,
      max_points: 5,
      earned_points: 0,
      status: 'incorrect',
      user_answer: 'x = 3',
      correct_answer: 'x = 4',
      explanation: 'log₂(x(x-2)) = 3 → x² - 2x = 8 → x² - 2x - 8 = 0 → (x-4)(x+2) = 0. x > 2 -ის გათვალისწინებით x = 4.',
    },
    {
      number: 10,
      max_points: 5,
      earned_points: 3,
      status: 'partial',
      user_answer: '35 სმ²',
      correct_answer: '35 სმ²',
      explanation: 'S = ½ × d₁ × d₂ × sin(α) = ½ × 10 × 14 × sin(30°) = ½ × 10 × 14 × 0.5 = 35. პასუხი სწორია, მაგრამ ამოხსნის გზა სრულად არ იყო ნაჩვენები.',
    },
  ],
};

const MOCK_EXAM_HISTORY: ExamAnalysisData = {
  exam_title: 'ისტორია — I ვარიანტი 2021',
  total_score: 42,
  max_score: 60,
  passing_score: 22,
  questions: [
    {
      number: 1,
      max_points: 2,
      earned_points: 2,
      status: 'correct',
      user_answer: '1864 წელი',
      correct_answer: '1864 წელი',
    },
    {
      number: 2,
      max_points: 5,
      earned_points: 3,
      status: 'partial',
      user_answer: 'რუსეთ-თურქეთის ომის შედეგად საქართველოს ნაწილი გათავისუფლდა ოსმალეთის ბატონობისგან.',
      correct_answer: 'სან-სტეფანოს ზავით და ბერლინის კონგრესით, ბათუმი და აჭარა რუსეთს შეუერთდა (1878). ბათუმს porto-franco სტატუსი მიენიჭა. ქარსი და არდაჰანიც რუსეთის შემადგენლობაში შევიდა.',
      explanation: 'კანდიდატმა ზოგადი მიმართულება სწორად მიუთითა, მაგრამ ვერ დაასახელა კონკრეტული ხელშეკრულებები (სან-სტეფანო, ბერლინის კონგრესი) და porto-franco სტატუსი.',
    },
    {
      number: 3,
      max_points: 5,
      earned_points: 5,
      status: 'correct',
      user_answer: 'ილია ჭავჭავაძე იყო ქართული ეროვნული მოძრაობის ლიდერი, „თერგდალეულთა" თაობის წინამძღოლი. დაარსა გაზეთი „ივერია", საქართველოს საბანკო საქმიანობის ფუძემდებელი, „ერის მამა".',
      correct_answer: 'ილია ჭავჭავაძე — მწერალი, პუბლიცისტი, საზოგადო მოღვაწე. „თერგდალეულთა" თაობის ლიდერი, გაზეთ „ივერიის" რედაქტორი, თავადაზნაურთა საადგილმამულო ბანკის თავმჯდომარე.',
    },
    {
      number: 4,
      max_points: 5,
      earned_points: 0,
      status: 'incorrect',
      user_answer: 'საქართველოში რევოლუცია მოხდა.',
      correct_answer: '1921 წლის 25 თებერვალს წითელი არმია თბილისში შევიდა. საქართველოს დემოკრატიული რესპუბლიკა დაეცა. მთავრობა ემიგრაციაში წავიდა (ჯერ ბათუმში, შემდეგ საფრანგეთში). საბჭოთა ხელისუფლება დამყარდა.',
      explanation: 'პასუხი ზედმეტად ზოგადი და არაზუსტია. „რევოლუცია" არასწორი ტერმინია — ეს იყო საბჭოთა რუსეთის სამხედრო ინტერვენცია/ოკუპაცია.',
    },
    {
      number: 5,
      max_points: 5,
      earned_points: 5,
      status: 'correct',
      user_answer: '1989 წლის 9 აპრილის მოვლენების შემდეგ, საქართველოში დაიწყო ეროვნული მოძრაობის ახალი ეტაპი. „მრგვალ მაგიდასთან" მოლაპარაკებებით მიღწეულ იქნა შეთანხმება მრავალპარტიული არჩევნების ჩატარებაზე 1990 წელს.',
      correct_answer: '„მრგვალი მაგიდა" — პოლიტიკური ძალების მოლაპარაკებები, რომლის შედეგადაც შეთანხმდნენ მრავალპარტიულ არჩევნებზე (1990 წ. 28 ოქტომბერი).',
    },
    {
      number: 6,
      max_points: 4,
      earned_points: 4,
      status: 'correct',
      user_answer: 'საქართველოს დემოკრატიული რესპუბლიკა (1918-1921). დამოუკიდებლობა გამოცხადდა 1918 წლის 26 მაისს. პირველი თავმჯდომარე ნოე ჟორდანია.',
      correct_answer: 'საქართველოს დემოკრატიული რესპუბლიკა (1918-1921), გამოცხადებული 26 მაისს, ნოე ჟორდანიას მთავრობით.',
    },
    {
      number: 7,
      max_points: 5,
      earned_points: 3,
      status: 'partial',
      user_answer: 'კოლხეთი ცნობილი იყო ოქროს საწმისის მითით. მნიშვნელოვანი სავაჭრო ცენტრი იყო.',
      correct_answer: 'კოლხეთის სამეფო (ძვ.წ. VI-I სს.) — მნიშვნელოვანი პოლიტიკური ერთეული, ოქროს მოპოვების ცენტრი, ბერძნული კოლონიზაციის არეალი (ფაზისი, დიოსკურია). არგონავტების მითის საფუძველი.',
      explanation: 'ზოგადად სწორი მიმართულებით, მაგრამ არ არის ნახსენები ბერძნული კოლონიები, ქრონოლოგია და პოლიტიკური მნიშვნელობა.',
    },
  ],
};

// --- Layout ---

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Card({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      {title && <h3 className="text-sm font-medium text-gray-900 mb-3">{title}</h3>}
      {children}
    </div>
  );
}

const TEXT_SECTIONS = [
  { title: 'Key Points', content: <KeyPoints data={MOCK_KEY_POINTS} /> },
  { title: 'Abilities', content: <Abilities data={MOCK_ABILITIES} /> },
  { title: 'Recommendations', content: <Recommendations data={MOCK_RECOMMENDATIONS} /> },
  { title: 'Compliance Adherence', content: <ComplianceAdherence data={MOCK_COMPLIANCE} /> },
  { title: 'Credibility & Consistency Check', content: <CredibilityCheck data={MOCK_CREDIBILITY} /> },
  { title: 'Custom Questions', content: <CustomQuestions data={MOCK_CUSTOM_QUESTIONS} /> },
];

const criterion = (
  key: string,
  name: string,
  score: number | null,
  evidence: string,
  source: 'ai' | 'measured' = 'ai',
  moments: IntegrityCriterion['moments'] = [],
): IntegrityCriterion => ({ key, name, score, evidence, source, moments });

const MOCK_INTEGRITY_FLAGGED: IntegrityReviewData = {
  status: 'completed',
  score: 5.5,
  review_recommended: true,
  summary: 'Looked down for long stretches with typing sounds before fluent, structured answers.',
  checked_at: '2026-10-30T19:00:00Z',
  criteria: [
    criterion('gaze_on_screen', 'Gaze on screen', 1, 'Looks down for long stretches before most answers.', 'ai', [
      { start: '01:34', end: '02:37', evidence: 'Looks down for a minute, then gives a fluent, structured answer.', benign_explanation: 'Thinking, or notes the interviewer allowed.' },
      { start: '05:45', end: '06:24', evidence: 'Looks down and away before a detailed answer.', benign_explanation: 'Concentrating.' },
    ]),
    criterion('reading_pattern', 'No reading', 4, 'A steady reading cadence after looking down.', 'ai', [
      { start: '02:38', end: '03:18', evidence: 'Delivers a list in a reading cadence.', benign_explanation: 'A rehearsed answer.' },
    ]),
    criterion('second_device', 'No second device', 6, 'Looks down often; no device visible.'),
    criterion('others_present', 'Alone in the session', 10, 'No one else seen or heard.'),
    criterion('other_voices', 'No other voices', 10, 'Only the interviewer and candidate are heard.'),
    criterion('typing_sounds', 'No typing while answering', 2, 'Typing during pauses before answers.', 'ai', [
      { start: '01:43', end: '01:55', evidence: 'Clear typing before speaking.', benign_explanation: 'Typing notes.' },
      { start: '04:46', end: '04:58', evidence: 'Loud typing during a long pause.', benign_explanation: 'Answering a message.' },
    ]),
    criterion('earpiece', 'No hidden earpiece', 10, 'No earpiece visible.'),
    criterion('screen_reflection', 'No screen reflections', null, 'Not wearing glasses; nothing to judge.'),
    criterion('response_timing', 'Natural response timing', 3, 'Long pauses, then sudden fluent answers.'),
    criterion('answer_naturalness', 'Natural answers', 5, 'Textbook, list-like answers.'),
    criterion('answer_specificity', 'Specific answers', 7, 'Lists tools without personal examples.'),
    criterion('consistency', 'Consistent answers', 10, 'Consistent throughout.'),
    criterion('camera_presence', 'Camera on', 9, 'Camera off or covered for 01:12 of 08:13.', 'measured'),
    criterion('tab_focus', 'Stayed on the session', 6, 'Left the session 3 times for 01:05 in total.', 'measured', [
      { start: '01:36', end: '02:10', evidence: 'Session tab hidden or window unfocused for 34s.', benign_explanation: 'Checking a notification.' },
    ]),
    criterion('copy_paste', 'No copy or paste', 8, '1 paste attempted (1 blocked).', 'measured'),
  ],
};

const MOCK_INTEGRITY_CLEAN: IntegrityReviewData = {
  status: 'completed',
  score: 9.8,
  review_recommended: false,
  summary: 'Kept eye contact and gave natural, specific answers throughout.',
  checked_at: '2026-10-30T19:00:00Z',
  criteria: (MOCK_INTEGRITY_FLAGGED.criteria ?? []).map((c) => ({ ...c, score: c.key === 'typing_sounds' ? 7 : 10, moments: [], evidence: c.key === 'typing_sounds' ? 'Faint tapping a few times; no pattern.' : 'No sign of it.' })),
};

function AdvancedBlocksPreview() {
  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <header className="mb-12">
          <h1 className="text-xl font-semibold text-gray-900 tracking-tight">Advanced Assessment</h1>
          <p className="text-sm text-gray-400 mt-1">Component preview — all states</p>
        </header>

        <div className="space-y-12">
          <Section title="Integrity Review">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <IntegrityReview review={MOCK_INTEGRITY_FLAGGED} />
              <IntegrityReview review={MOCK_INTEGRITY_CLEAN} />
            </div>
          </Section>

          {/* Scored components */}
          <Section title="Integrity Detection">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CheatDetection data={MOCK_CHEAT_CLEAR} />
              <CheatDetection data={MOCK_CHEAT_SUSPICIOUS} />
              <CheatDetection data={MOCK_CHEAT_LIKELY} />
            </div>
          </Section>

          <Section title="Body Language">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BodyLanguage data={MOCK_BODY_LANGUAGE} />
              <BodyLanguage data={MOCK_BODY_LANGUAGE_HIGH} />
            </div>
          </Section>

          <Section title="Filler Words">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FillerWords data={MOCK_FILLER_WORDS} />
              <FillerWords data={MOCK_FILLER_WORDS_GOOD} />
            </div>
          </Section>

          {/* Text-based components */}
          {TEXT_SECTIONS.map(({ title, content }) => (
            <Section key={title} title={title}>
              <Card>{content}</Card>
            </Section>
          ))}

          <Section title="Assessment Scores">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card title="Low-Mid Scores">
                <AssessmentScores data={MOCK_ASSESSMENT_SCORES} />
              </Card>
              <Card title="High Scores">
                <AssessmentScores data={MOCK_ASSESSMENT_SCORES_HIGH} />
              </Card>
            </div>
          </Section>

          <Section title="Assessment Summary">
            <Card>
              <AssessmentSummary data={MOCK_ASSESSMENT_SUMMARY} />
            </Card>
          </Section>

          {/* Exam Analysis */}
          <Section title="Exam Analysis">
            <div className="space-y-6">
              <Card title="მათემატიკა — ერთიანი ეროვნული გამოცდა">
                <ExamAnalysis data={MOCK_EXAM_MATH} />
              </Card>
              <Card title="ისტორია — I ვარიანტი 2021">
                <ExamAnalysis data={MOCK_EXAM_HISTORY} />
              </Card>
            </div>
          </Section>

          {/* Integrated locked/unlocked */}
          <Section title="Integrated — Locked / Unlocked">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AdvancedAssessmentBlock
                block={{ name: 'AI Cheat Detection', key: 'ai_cheat_detection', score: 52, output_type: 'component', component_data: MOCK_CHEAT_SUSPICIOUS as any }}
                isLocked={false}
                onUnlock={() => {}}
              />
              <AdvancedAssessmentBlock
                block={{ name: 'Body Language', key: 'body_language', score: 72, output_type: 'component', component_data: MOCK_BODY_LANGUAGE as any }}
                isLocked={true}
                onUnlock={() => alert('Unlock')}
              />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
